import { PaletteMode, Components, Theme } from '@mui/material';
import { themePalette } from '../theme.palette';

export const MuiSwitchTheme = (mode: PaletteMode): Components<Theme>['MuiSwitch'] => {
  const { componentTokens, tokens } = themePalette(mode);

  return {
    defaultProps: {
      disableRipple: true,
      disableFocusRipple: true,
      disableTouchRipple: true,
    },
    styleOverrides: {
      root: {
        width: 32,
        height: 20,
        padding: 0,
        overflow: 'visible',
      },
      switchBase: {
        padding: 0,
        top: 4,
        left: 4,
        transition: 'transform 200ms cubic-bezier(0.0, 0, 0.2, 1) 0ms',

        // Unchecked state
        '& .MuiSwitch-thumb': {
          backgroundColor: componentTokens.selectors.colors.toggle.off.assets.resting,
          transition: 'background-color 0.15s linear',
        },

        // Hover on unchecked
        '&:hover .MuiSwitch-thumb': {
          backgroundColor: componentTokens.selectors.colors.toggle.off.assets.hover,
        },

        // Focus on unchecked
        '&.Mui-focusVisible .MuiSwitch-thumb': {
          backgroundColor: componentTokens.selectors.colors.toggle.off.assets.hover,
        },

        // Checked state
        '&.Mui-checked': {
          '& .MuiSwitch-thumb': {
            backgroundColor: componentTokens.selectors.colors.toggle.on.assets.resting,
          },
          transform: 'translateX(12px)',
          '& + .MuiSwitch-track': {
            backgroundColor: componentTokens.selectors.colors.toggle.on.background.resting,
            opacity: 1,
          },
          // Hover on checked
          '&:hover .MuiSwitch-thumb': {
            backgroundColor: componentTokens.selectors.colors.toggle.on.assets.hover,
          },
          '&:hover + .MuiSwitch-track': {
            backgroundColor: componentTokens.selectors.colors.toggle.on.background.hover,
          },
          // Focus on checked
          '&.Mui-focusVisible .MuiSwitch-thumb': {
            backgroundColor: componentTokens.selectors.colors.toggle.on.assets.hover,
          },
          '&.Mui-focusVisible + .MuiSwitch-track': {
            backgroundColor: componentTokens.selectors.colors.toggle.on.background.hover,
          },
        },

        // Disabled state
        '&.Mui-disabled': {
          '& + .MuiSwitch-track': {
            opacity: 0.8,
          },
          '& .MuiSwitch-thumb': {
            opacity: 0.8,
          },
        },

        // Invalid/error state
        '&.MuiSwitch-colorError': {
          '& + .MuiSwitch-track': {
            backgroundColor: tokens.error,
            opacity: 1,
          },
          '&:hover + .MuiSwitch-track': {
            backgroundColor: tokens.error,
          },
          '&.Mui-focusVisible + .MuiSwitch-track': {
            backgroundColor: tokens.error,
          },
        },
      },
      thumb: {
        width: 12,
        height: 12,
        backgroundColor: componentTokens.selectors.colors.toggle.off.assets.resting,
        boxShadow: 'none',
      },
      track: {
        border: '1px solid',
        borderRadius: 20 / 2, // Half of height for pill shape
        backgroundColor: componentTokens.selectors.colors.toggle.off.background.resting,
        borderColor: componentTokens.selectors.colors.toggle.off.stroke.resting,
        opacity: 1,
        transition: 'background-color 0.15s linear, border-color 0.15s linear',

        // Hover on unchecked track
        '.MuiSwitch-switchBase:hover + &': {
          backgroundColor: componentTokens.selectors.colors.toggle.off.background.hover,
          borderColor: componentTokens.selectors.colors.toggle.off.stroke.hover,
        },

        // Focus on unchecked track
        '.MuiSwitch-switchBase.Mui-focusVisible + &': {
          backgroundColor: componentTokens.selectors.colors.toggle.off.background.hover,
          borderColor: componentTokens.selectors.colors.toggle.off.stroke.hover,
        },

        // Checked track (handled in switchBase.Mui-checked)
        '.MuiSwitch-switchBase.Mui-checked + &': {
          backgroundColor: componentTokens.selectors.colors.toggle.on.background.resting,
          borderColor: componentTokens.selectors.colors.toggle.on.stroke.resting,
        },

        // Hover on checked track
        '.MuiSwitch-switchBase.Mui-checked:hover + &': {
          backgroundColor: componentTokens.selectors.colors.toggle.on.background.hover,
          borderColor: componentTokens.selectors.colors.toggle.on.stroke.hover,
        },

        // Focus on checked track
        '.MuiSwitch-switchBase.Mui-checked.Mui-focusVisible + &': {
          backgroundColor: componentTokens.selectors.colors.toggle.on.background.hover,
          borderColor: componentTokens.selectors.colors.toggle.on.stroke.hover,
        },
      },
    },
  };
};
